import {
  Component, EventEmitter,
  Input,
  OnDestroy,
  OnInit, Output,
  TrackByFunction,
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { BehaviorSubject, tap, concatMap, Subscription, finalize } from 'rxjs';

import {
  SharedTableColumn,
  SharedTableDataSource,
  SharedTableItemType,
} from './shared-table.interfaces';
import { AppIcon } from '@tablesready/frontend-icons';
import { MAX_DISPLAY_PAGES_MOBILE } from '@shared/core';

@Component({
  selector: 'tablesready-shared-table',
  templateUrl: './shared-table.component.html',
  styleUrls: ['./shared-table.component.scss'],
})
export class SharedTableComponent<T extends { _id: string }> implements OnInit, OnDestroy {
  @Output() dataLoaded = new EventEmitter<boolean>();
  @Input() data: T[] = [];
  @Input() dataSource?: SharedTableDataSource<T>;
  @Input() columns: SharedTableColumn[] = [];
  @Input() enablePagination = true;
  @Input() frontEndPagination = false;
  @Input() timezone = '';
  @Input() showHeader = true;
  @Input() customRowClass = '';
  @Input() customCellClass = '';

  icon = AppIcon;
  sortingBy = '';
  sortDirection = 'DESC';
  dataType = SharedTableItemType;
  lastParams: Record<string, unknown> = { search: '' };

  private readonly subscription = new Subscription();
  private readonly loadData$ = new BehaviorSubject<Record<string, unknown>>(this.lastParams);

  readonly trackById: TrackByFunction<T> = (idx, { _id }) => _id;

  constructor(private readonly breakPointObserver: BreakpointObserver) {}

  ngOnInit(): void {
    if (!this.dataSource) {
      return;
    }

    const loadDataSub = this.loadData$
      .pipe(
        concatMap(params => this.dataSource.loadData(params)),
      )
      .subscribe((res) => {
        this.dataLoaded.emit(true);

        if (this.frontEndPagination) {
          const { page, pageSize } = this.dataSource;
          this.data = res.slice((page - 1) * pageSize, ((page - 1) * pageSize) + pageSize);

          return;
        }

        this.data = res;
      });
    this.subscription.add(loadDataSub);

    const breakpointObserverSub = this.breakPointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape])
      .pipe(tap(({ matches: isMobile }) => {
        this.dataSource.maxDisplayPages = isMobile ? MAX_DISPLAY_PAGES_MOBILE : this.dataSource.maxDisplayPages;
      })).subscribe();
    this.subscription.add(breakpointObserverSub);
  }

  sortBy(columnName: string): void {
    if (this.sortingBy !== columnName) {
      this.sortingBy = columnName;
      this.sortDirection = 'DESC';
    } else if (this.sortingBy === columnName && this.sortDirection === 'DESC') {
      this.sortDirection = 'ASC';
    } else if (this.sortingBy === columnName && this.sortDirection === 'ASC') {
      this.sortingBy = '';
    }

    this.loadData({
      orderColumn: this.sortingBy === '' ? undefined : this.sortingBy,
      orderDirection: this.sortingBy === '' ? undefined : this.sortDirection,
    }, true);
  }

  loadData(params: Record<string, unknown>, resetPage: boolean): void {
    if (!this.dataSource) {
      return;
    }

    if (resetPage) {
      this.dataSource.page = 1;
      params.page = 1;
    }

    if (params.planId === this.lastParams.planId) {
      this.lastParams = {
        ...this.lastParams,
        ...params,
      };
    } else {
      this.lastParams = params;
    }

    this.loadData$.next(this.lastParams);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
